/**
 * WebGPU Lyrics Overlay Module
 *
 * Creates a screen-space overlay plane for rendering lyrics directly in the
 * WebGPU scene. This avoids GPU readback by compositing lyrics on the GPU
 * instead of reading back the visualization, compositing on CPU, then encoding.
 *
 * The overlay uses:
 * - A separate Scene and OrthographicCamera for 2D overlay rendering
 * - A PlaneGeometry covering the full viewport
 * - A CanvasTexture updated from the lyrics canvas each frame
 * - Rendered after the main scene with autoClear disabled
 */

import * as THREE from 'three/webgpu';

/**
 * Creates and manages a lyrics overlay for WebGPU visualizers
 */
export class WebGPULyricsOverlay {
  /**
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   */
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.disposed = false;

    // Create overlay scene and camera
    this.scene = new THREE.Scene();

    // Orthographic camera covering NDC space (-1 to 1)
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    // Create fullscreen plane in NDC space
    this.geometry = new THREE.PlaneGeometry(2, 2);

    // Material with transparency, no depth testing
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.FrontSide,
      opacity: 1,
    });

    // Create mesh and add to scene
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(this.mesh);

    // Texture will be created on first update
    this.texture = null;
  }

  /**
   * Updates the overlay texture from a lyrics canvas
   * @param {OffscreenCanvas|HTMLCanvasElement} lyricsCanvas - Canvas with rendered lyrics
   */
  updateTexture(lyricsCanvas) {
    if (this.disposed || !lyricsCanvas) return;

    if (!this.texture) {
      // Create texture on first update
      this.texture = new THREE.CanvasTexture(lyricsCanvas);
      this.texture.colorSpace = THREE.SRGBColorSpace;
      this.texture.minFilter = THREE.LinearFilter;
      this.texture.magFilter = THREE.LinearFilter;
      this.texture.generateMipmaps = false;
      this.material.map = this.texture;
      this.material.needsUpdate = true;
    } else {
      // Update existing texture
      // For CanvasTexture, we need to update the image source
      this.texture.image = lyricsCanvas;
      this.texture.needsUpdate = true;
    }
  }

  /**
   * Renders the overlay using the given renderer
   * Call this AFTER rendering the main scene with autoClear = false
   * @param {THREE.WebGPURenderer} renderer - The WebGPU renderer
   */
  async render(renderer) {
    if (this.disposed || !this.texture) return;

    // Render overlay scene on top
    await renderer.renderAsync(this.scene, this.camera);
  }

  /**
   * Clears the overlay (hides it)
   */
  clear() {
    if (this.material) {
      this.material.map = null;
      this.material.needsUpdate = true;
    }
  }

  /**
   * Resizes the overlay for new dimensions
   * @param {number} width - New width
   * @param {number} height - New height
   */
  resize(width, height) {
    this.width = width;
    this.height = height;
    // PlaneGeometry in NDC space doesn't need resizing
    // Texture aspect is handled by the source canvas
  }

  /**
   * Disposes of all resources
   */
  dispose() {
    if (this.disposed) return;
    this.disposed = true;

    if (this.texture) {
      this.texture.dispose();
      this.texture = null;
    }

    if (this.material) {
      this.material.dispose();
      this.material = null;
    }

    if (this.geometry) {
      this.geometry.dispose();
      this.geometry = null;
    }

    if (this.scene) {
      this.scene.remove(this.mesh);
      this.scene = null;
    }

    this.mesh = null;
    this.camera = null;
  }
}
